"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "AuditMigration", {
    enumerable: true,
    get: function() {
        return AuditMigration;
    }
});
const _uuid = require("uuid");
const _common = require("@nestjs/common");
const _nocodbsdk = require("nocodb-sdk");
const _globals = require("~/utils/globals");
const _Noco = /*#__PURE__*/ _interop_require_default(require("~/Noco"));
const _ncconfig = require("~/utils/nc-config");
function _interop_require_default(obj) {
    return obj && obj.__esModule ? obj : {
        default: obj
    };
}
function _ts_decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
let AuditMigration = class AuditMigration {
    async job() {
        try {
            this.logger.log('Starting audit migration job');
            const ncMeta = _Noco.default.ncMeta;
            // Use smaller batch size for SQLite due to "too many terms in compound SELECT" issue
            // ref: https://www.sqlite.org/limits.html#max_compound_select
            const batchSize = ncMeta.knex.clientType() === _ncconfig.DriverClient.SQLITE ? 100 : 1000;
            const fallbackTimestamp = new Date('2020-01-01T00:00:00.000Z').getTime();
            const haveColumn = await ncMeta.knexConnection.schema.hasColumn(_globals.MetaTable.AUDIT, 'old_id');
            if (!haveColumn) {
                this.logger.log('Audit table already migrated');
                return true;
            }
            const lastMigratedRecord = await ncMeta.knexConnection(_globals.MetaTable.AUDIT).whereNotNull('old_id').orderBy('created_at', 'desc').orderBy('old_id', 'desc').first();
            let lastProcessedTimestamp = (lastMigratedRecord == null ? void 0 : lastMigratedRecord.created_at) || new Date(0);
            let lastProcessedId = (lastMigratedRecord == null ? void 0 : lastMigratedRecord.old_id) || 0;
            this.logger.log(`Starting from timestamp: ${lastProcessedTimestamp}, last ID: ${lastProcessedId}`);
            // Use compound cursor (timestamp + id) to handle duplicate timestamps correctly
            const totalRemainingRecords = await ncMeta.knexConnection.from(`${_globals.MetaTable.AUDIT}_old as old`).leftJoin(`${_globals.MetaTable.AUDIT} as new`, 'old.id', 'new.old_id').whereNull('new.old_id').where('old.op_type', '!=', _nocodbsdk.AuditOperationTypes.COMMENT).where(function() {
                this.where('old.created_at', '>', lastProcessedTimestamp).orWhere(function() {
                    this.where('old.created_at', '=', lastProcessedTimestamp).andWhere('old.id', '>', lastProcessedId);
                });
            }).count('old.id as count').first();
            if (totalRemainingRecords.count === 0) {
                this.logger.log('No more records to migrate, starting cleanup');
                await this.cleanupMigration(ncMeta);
                this.logger.log('Audit migration completed successfully');
                return true;
            }
            this.logger.log(`Found ${totalRemainingRecords.count} records to migrate in this batch`);
            let processedCount = 0;
            let totalMigratedCount = 0;
            let hasMoreRecords = true;
            while(hasMoreRecords){
                // Use compound cursor to avoid infinite loops with duplicate timestamps
                const batch = await ncMeta.knexConnection.select('*').from(`${_globals.MetaTable.AUDIT}_old`).where(function() {
                    this.where('created_at', '>', lastProcessedTimestamp).orWhere(function() {
                        this.where('created_at', '=', lastProcessedTimestamp).andWhere('id', '>', lastProcessedId);
                    });
                }).orderBy('created_at', 'asc').orderBy('id', 'asc').limit(batchSize);
                if (batch.length === 0) {
                    hasMoreRecords = false;
                    break;
                }
                this.logger.debug(`Processing batch of ${batch.length} records`);
                const auditRecords = [];
                const oldIds = new Set();
                for (const record of batch){
                    if (record.op_type === _nocodbsdk.AuditOperationTypes.COMMENT) {
                        continue;
                    }
                    let timestamp = fallbackTimestamp;
                    if (record.created_at) {
                        const createdAtDate = new Date(record.created_at);
                        if (!isNaN(createdAtDate.getTime())) {
                            timestamp = createdAtDate.getTime();
                        }
                    }
                    // Generate UUIDv7 with preserved timestamp for chronological ordering
                    const id = (0, _uuid.v7)({
                        msecs: timestamp
                    });
                    const newRecord = {
                        id,
                        user: record.user,
                        ip: record.ip,
                        source_id: record.source_id,
                        base_id: record.base_id,
                        fk_model_id: record.fk_model_id,
                        row_id: record.row_id,
                        op_type: record.op_type,
                        op_sub_type: record.op_sub_type,
                        status: record.status,
                        description: record.description,
                        details: record.details,
                        fk_user_id: record.fk_user_id,
                        fk_ref_id: record.fk_ref_id,
                        fk_parent_id: null,
                        fk_workspace_id: record.fk_workspace_id,
                        fk_org_id: null,
                        user_agent: record.user_agent,
                        version: record.version,
                        created_at: record.created_at,
                        updated_at: record.updated_at,
                        old_id: record.id
                    };
                    auditRecords.push(newRecord);
                    oldIds.add(record.id);
                }
                if (auditRecords.length > 0) {
                    await ncMeta.knexConnection.transaction(async (trx)=>{
                        // Check for parallel migration conflicts
                        const existingRecords = await trx(_globals.MetaTable.AUDIT).whereIn('old_id', Array.from(oldIds)).select('old_id');
                        const existingIds = new Set(existingRecords.map((r)=>r.old_id));
                        const newRecords = auditRecords.filter((r)=>!existingIds.has(r.old_id));
                        if (newRecords.length > 0) {
                            await trx(_globals.MetaTable.AUDIT).insert(newRecords);
                            totalMigratedCount += newRecords.length;
                            this.logger.debug(`Inserted ${newRecords.length} new audit records`);
                        }
                        if (existingIds.size > 0) {
                            this.logger.debug(`Skipped ${existingIds.size} already migrated records`);
                        }
                    });
                }
                // Update cursor to last record in batch
                const lastRecord = batch[batch.length - 1];
                lastProcessedTimestamp = lastRecord.created_at;
                lastProcessedId = lastRecord.id;
                processedCount += batch.length;
                if (processedCount % 5000 === 0) {
                    const progressPercentage = +totalRemainingRecords.count > 0 ? (processedCount / +totalRemainingRecords.count * 100).toFixed(1) : 0;
                    this.logger.log(`Migration progress: ${processedCount}/${totalRemainingRecords.count} records processed (${progressPercentage}%), ${totalMigratedCount} records migrated`);
                }
                if (batch.length < batchSize) {
                    hasMoreRecords = false;
                }
            }
            this.logger.log(`Migration batch completed. Processed ${processedCount} records, migrated ${totalMigratedCount} new records.`);
            // Check completion (excluding comment records that are intentionally skipped)
            const remainingRecords = await ncMeta.knexConnection.from(`${_globals.MetaTable.AUDIT}_old as old`).leftJoin(`${_globals.MetaTable.AUDIT} as new`, 'old.id', 'new.old_id').whereNull('new.old_id').where('old.op_type', '!=', _nocodbsdk.AuditOperationTypes.COMMENT).count('old.id as count').first();
            this.logger.log(`Remaining unmigrated records: ${remainingRecords.count}`);
            if (remainingRecords.count === 0) {
                this.logger.log('All records migrated, starting cleanup');
                await this.cleanupMigration(ncMeta);
                this.logger.log('Audit migration completed successfully');
                return true;
            }
            this.logger.log('Migration batch completed, more records remain for next run');
            return false;
        } catch (e) {
            this.logger.error('Error running audit migration', e.stack || e.message);
            return false;
        }
    }
    async cleanupMigration(ncMeta) {
        try {
            this.logger.log('Starting migration cleanup');
            // TODO: skipped for this iteration, we will drop in a follow up
            // await ncMeta.knexConnection.schema.dropTable(`${MetaTable.AUDIT}_old`);
            // this.logger.log('Dropped old audit table');
            await ncMeta.knexConnection.schema.alterTable(_globals.MetaTable.AUDIT, (table)=>{
                table.dropColumn('old_id');
            });
            this.logger.log('Removed old_id column from audit table');
            this.logger.log('Migration cleanup completed successfully');
        } catch (e) {
            this.logger.error('Error during migration cleanup', e.stack || e.message);
            throw e;
        }
    }
    constructor(){
        this.logger = new _common.Logger(AuditMigration.name);
    }
};
AuditMigration = _ts_decorate([
    (0, _common.Injectable)()
], AuditMigration);
